<template>
  <confirm-dialog
    ref="confirm"
    :title="confirmDialogTitle"
    :confirmCb="saveShipment"
    :width="60"
  >
    <template v-slot:content>
      <div v-loading="loadingShipment">
        <div class="common-hint-list">
          <span>Please check carefully before saving!</span>
          <span
            >Pallet in other shipments will not be included in creatted
            shipment!</span
          >
        </div>
        <div class="pick-up-date" v-if="inCreationMode">
          <span class="demonstration pick-up-date-title">Pick Up Date</span>
          <el-date-picker
            v-model="pickUpDate"
            size="medium"
            type="date"
            placeholder="Select pick up date"
          >
          </el-date-picker>
        </div>
        <div class="f-r f-r-hsb summary-list">
          <div class="difference f-c" v-if="mode === ShipmentUpdate">
            <div class="added summary-group">
              <div class="summary-title f-r f-r-hsb">
                <span class="summary-name">New</span>
                <span class="total">
                  Sum:
                  {{ countPallets(addedGroups) }}
                </span>
              </div>
              <div
                v-for="group in addedGroups"
                :key="group.id"
                class="f-r f-r-vc each-summary"
              >
                <address-tag class="tag" :address="group.address" />
                <div class="count">
                  Pallet Count: {{ group.palletIds.length }}
                </div>
              </div>
            </div>
            <div class="removed summary-group">
              <div class="summary-title f-r f-r-hsb">
                <span class="summary-name">Removed</span>
                <span class="total">
                  Sum:
                  {{ countPallets(removedGroups) }}
                </span>
              </div>
              <div
                v-for="group in removedGroups"
                :key="group.id"
                class="f-r f-r-vc each-summary"
              >
                <address-tag class="tag" :address="group.address" />
                <div class="count">
                  Pallet Count: {{ group.palletIds.length }}
                </div>
              </div>
            </div>
          </div>
          <div class="all summary-group">
            <div class="summary-title f-r f-r-hsb">
              <span class="summary-name">All</span>
              <span class="total">
                Sum:
                {{ countPallets(palletGroups) }}
              </span>
            </div>
            <div
              v-for="group in palletGroups"
              :key="group.id"
              class="each-summary"
              :class="{ 'new-group': isNewAddGroup(group.id) }"
            >
              <address-tag class="tag" :address="group.address" />
              <div class="count">
                Pallet Count: {{ countGroupPallets(group) }} /
                {{ group.palletIds.length }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </confirm-dialog>
</template>
<script>
import { ShipmentCreation, ShipmentUpdate } from '@/core/models/shipment'
import ConfirmDialog from '@/components/common/Confirm.vue'
import AddressTag from '@/components/address/AddressTag.vue'
import {
  createShipment,
  modifyShipmentPallets,
  queryShipment
} from '@/api/shipment'
import { queryPallets, searchStock } from '@/api/pallet'
import { queryAddress } from '@/api/address'
// mixin
import { pageJumps } from '@/core/mixin/pages'
import { TrueStatus } from '@/core/configs/types'
export default {
  name: 'ShipmentConfirm',
  components: {
    ConfirmDialog,
    AddressTag
  },
  mixins: [pageJumps],
  props: {
    mode: {
      required: true
    },
    shipmentNo: {
      default: ''
    },
    palletGroups: {
      type: Array,
      required: true
    }
  },
  computed: {
    inCreationMode() {
      return this.mode === ShipmentCreation
    },
    confirmDialogTitle() {
      return this.mode === ShipmentCreation
        ? 'Create New Shipment'
        : 'Update Shipment ' + this.shipmentNo
    },
    removedGroups() {
      if (this.mode === ShipmentUpdate) {
        return this.palletGroupsOrigin
          .map(group => {
            const index = this.palletGroups.findIndex(
              inner => inner.id === group.id
            )
            if (index > -1) {
              const curGroup = this.palletGroups[index]
              const originIds = new Set(group.palletIds)
              const newIds = new Set(curGroup.palletIds)
              return {
                ...group,
                palletIds: [...originIds].filter(id => !newIds.has(id))
              }
            } else {
              return group
            }
          })
          .filter(one => one.palletIds.length > 0)
      }
      return []
    },
    addedGroups() {
      if (this.mode === ShipmentUpdate) {
        return this.palletGroups
          .map(group => {
            const index = this.palletGroupsOrigin.findIndex(
              inner => inner.id === group.id
            )
            if (index > -1) {
              const oriGroup = this.palletGroupsOrigin[index]
              const curIds = new Set(group.palletIds)
              const originIds = new Set(oriGroup.palletIds)
              return {
                ...group,
                palletIds: [...curIds].filter(id => !originIds.has(id))
              }
            } else {
              return group
            }
          })
          .filter(one => one.palletIds.length > 0)
      }
      return []
    },
    excludedGroups() {
      const groupMap = {}
      this.excludePallets.forEach(pallet => {
        const groupIndex = this.palletGroups.findIndex(group =>
          group.palletIds.includes(pallet.id)
        )
        if (!(groupIndex in groupMap)) {
          groupMap[groupIndex] = {
            ...this.palletGroups[groupIndex],
            palletIds: []
          }
        }
        groupMap[groupIndex].palletIds.push(pallet.id)
      })
      return Object.values(groupMap)
    }
  },
  data() {
    return {
      ShipmentCreation,
      ShipmentUpdate,
      loadingShipment: false,
      shipmentOrigin: null,
      palletGroupsOrigin: [],
      invalidGroupPallets: [],
      excludePallets: [],
      pickUpDate: null
    }
  },
  methods: {
    open() {
      if (this.mode === ShipmentUpdate) this.getShipmentDetail()
      this.checkSelectedPallets()
      this.$refs.confirm.open()
    },
    isNewAddGroup(groupId) {
      return this.palletGroupsOrigin.findIndex(one => one.id === groupId) === -1
    },
    countPallets(groups) {
      return groups.reduce((one, two) => one + two.palletIds.length, 0)
    },
    countGroupPallets(group) {
      const excludeGroup = this.excludedGroups.find(
        one =>
          one.orderId === group.orderId && one.groupName === group.groupName
      )
      if (excludeGroup) {
        return group.palletIds.length - excludeGroup.palletIds.length
      }
      return group.palletIds.length
    },
    saveShipment(done, error) {
      if (this.inCreationMode && this.pickUpDate == null) {
        error('Pick up date is required!')
        return
      }
      if (this.mode === ShipmentCreation) {
        this.createNewShipment(done, error)
      } else {
        this.updateShipment(done, error)
      }
    },
    async getShipmentDetail() {
      this.loadingShipment = true
      const page = await queryShipment({ nos: [this.shipmentNo] })
      const shipmentDetail = page.records[0]
      // make current cartList to pallets in shipment
      const selectedStock = await searchStock({
        shipmentId: shipmentDetail.id,
        onlyShipped: true,
        size: -1
      })
      const addressIds = selectedStock.records
        .filter(one => !!one.addressId)
        .map(one => one.addressId)
      const addressPage = await queryAddress({ addressIds, size: -1 })
      const addressMap = Object.fromEntries(
        addressPage.records.map(one => [one.id, one])
      )
      const palletGroups = selectedStock.records.map(one => {
        return {
          id: one.orderId + '/' + one.groupName + '/' + one.addressId,
          goodsIds: one?.goodsIds?.split(',') ?? [],
          orderId: one.orderId,
          riiOrderNo: one.riiOrderNo,
          riiContainerNo: one.riiContainerNo,
          address: addressMap[one.addressId],
          palletIds: one?.palletIds?.split(',') ?? []
        }
      })
      this.$set(this, 'shipmentOrigin', shipmentDetail)
      this.$set(this, 'palletGroupsOrigin', palletGroups)
      this.loadingShipment = false
    },
    async createNewShipment(done, error) {
      this.loadingShipment = true
      const excludeIds = this.excludePallets.map(one => one.id)
      createShipment({
        pickUpDate: this.pickUpDate,
        groups: this.palletGroups.map(one => ({
          orderId: one.orderId,
          addressId: one.address.id,
          palletIds: one.palletIds.filter(one => !excludeIds.includes(one))
        }))
      })
        .then(res => {
          done()
          localStorage.setItem('cartList', JSON.stringify([]))
          this.goToShipmentDetail(res.no)
        })
        .catch(err => error(err))
        .finally(() => {
          this.loadingShipment = false
        })
    },
    async updateShipment(done, error) {
      this.loadingShipment = true
      const palletsAdd = this.addedGroups.map(one => one.palletIds).flat()
      const palletsRemove = this.removedGroups.map(one => one.palletIds).flat()
      if (palletsAdd.length === 0 && palletsRemove.length === 0) {
        this.$message.warning('Nothing changed!')
      } else {
        modifyShipmentPallets(this.shipmentOrigin.id, palletsAdd, palletsRemove)
          .then(res => {
            done()
            localStorage.removeItem(this.shipmentNo)
            this.$message.success('Successfully updated shipment!')
            this.goToShipmentDetail(this.shipmentNo)
          })
          .catch(err => error(err))
          .finally(() => {
            this.loadingShipment = false
          })
      }
    },
    async checkSelectedPallets() {
      // check if any selected pallets are in shipments before creation
      const palletIds = this.palletGroups.reduce((prev, one) => {
        prev.push(...one.palletIds)
        return prev
      }, [])
      queryPallets({
        ids: palletIds,
        inShipment: TrueStatus,
        size: -1
      }).then(res => {
        if (res) {
          let pallets = res.records
          if (this.mode === ShipmentUpdate) {
            pallets = pallets.filter(one => one.shipmentNo !== this.shipmentNo)
          }
          pallets.forEach(one => {
            const groupIndex = this.palletGroups.findIndex(group =>
              group.palletIds.includes(one.id)
            )
            one.groupIndex = groupIndex
          })
          this.$set(this, 'excludePallets', pallets)
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.summary-list {
  gap: 20px;
  min-height: 200px;

  .each-summary {
    height: 32px !important;
    padding: 6px 20px;
    border-radius: 3px;
    border: 1px solid #ccc;

    @extend .f-r, .f-r-vc, .f-r-hsb;

    .tag {
      font-size: 18px !important;

      ::v-deep .address {
        font-size: 18px;
      }
    }

    .count {
      color: black;
      margin-left: 40px;
      font-size: 18px;
    }
  }

  .summary-group {
    gap: 5px;
    margin-bottom: 20px;

    @extend .f-c;

    .summary-name,
    .total {
      font-size: 21px;

      @extend .bold-oswald;
    }
  }

  .removed {
    .summary-name {
      color: $deep-danger;
    }

    .each-summary {
      background-color: rgba($deep-danger, 0.1) !important;
    }
  }

  .all,
  .difference {
    flex-grow: 1;
  }

  .new-group {
    background-color: rgba($deep-green, 0.1);
  }
}

.pick-up-date-title {
  font-size: 21px;
  margin-right: 20px;
  @extend .bold-oswald;
}
</style>
